import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PrimaryButton } from 'Core/components/Button';
import { AlertError } from 'Core/components/Alert';
import useConfirmSetup from 'Core/hooks/queries/stripe/useConfirmSetup';
import { stripeFor } from 'Core/utils/stripe';
import { messageOf } from 'Modules/checkout/api';
import { checkoutUrl } from 'Modules/checkout/hooks/useStripeReturn';

const CARD_CONSENT =
  'Ich ermächtige audibene, meine Karte bei Rechnungsstellung einmalig mit dem Rechnungsbetrag zu belasten.';

const SUBMIT = {
  CARD: { key: 'checkout.saveMethod.CARD.submit', label: 'Kreditkarte speichern' },
  SEPA: { key: 'checkout.saveMethod.SEPA.submit', label: 'Mandat erteilen' },
};

export const PAYMENT_ELEMENT_OPTIONS = {
  layout: { type: 'tabs', defaultCollapsed: false },
  wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
};

export const fullNameOf = (customer) =>
  [customer?.given_name, customer?.family_name].filter(Boolean).join(' ');

const addressOf = (customer) => {
  const address = customer?.billing_address;
  if (!address) {
    return null;
  }
  return {
    line1: address.address_line1 || null,
    postal_code: address.postal_code || null,
    city: address.city || null,
    country: address.country || null,
  };
};

export const billingDefaultsOf = (customer, withName) => {
  const billingDetails = {
    ...(withName ? { name: fullNameOf(customer) } : {}),
    ...(customer?.email ? { email: customer.email } : {}),
    ...(addressOf(customer) ? { address: addressOf(customer) } : {}),
  };
  return { ...PAYMENT_ELEMENT_OPTIONS, defaultValues: { billingDetails } };
};

function SaveMethodForm({ method, customer, onDone }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const confirm = useConfirmSetup();
  const submitLabel = SUBMIT[method] || SUBMIT.CARD;
  const [holder, setHolder] = useState(fullNameOf(customer));
  const askHolder = method === 'CARD';
  const options = billingDefaultsOf(customer, !askHolder);

  const submit = (event) => {
    event.preventDefault();
    if (!stripe || !elements || (askHolder && !holder.trim())) {
      return;
    }
    const billingDetails = askHolder ? { name: holder.trim() } : undefined;
    confirm.mutate({ stripe, elements, returnUrl: checkoutUrl(), billingDetails }, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} data-testid="save-method">
      {askHolder && (
        <label className="block mb-3">
          <span className="block text-sm text-gray-700 mb-1">
            {t('checkout.saveMethod.CARD.holder', 'Name des Karteninhabers')}
          </span>
          <input
            type="text"
            autoComplete="cc-name"
            value={holder}
            onChange={(event) => setHolder(event.target.value)}
            data-testid="card-holder"
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </label>
      )}
      <PaymentElement options={options} />
      {method === 'CARD' && (
        <p className="text-sm text-gray-700 mt-4" data-testid="card-consent">
          {t('checkout.saveMethod.CARD.consent', CARD_CONSENT)}
        </p>
      )}
      {confirm.isError && (
        <div className="mt-3" data-testid="stripe-error">
          <AlertError>{messageOf(confirm.error)}</AlertError>
        </div>
      )}
      <div className="flex justify-end mt-4">
        <PrimaryButton
          type="submit"
          data-testid="save-method-submit"
          disabled={!stripe || !elements || confirm.isLoading || (askHolder && !holder.trim())}
        >
          {t(submitLabel.key, submitLabel.label)}
        </PrimaryButton>
      </div>
    </form>
  );
}

export default function SaveMethod({ method, customer, clientSecret, publishableKey, onDone }) {
  return (
    <Elements
      key={clientSecret}
      stripe={stripeFor(publishableKey)}
      options={{ clientSecret, locale: 'de' }}
    >
      <SaveMethodForm method={method} customer={customer} onDone={onDone} />
    </Elements>
  );
}
